import { StopCause, TimeSpan } from "../Types/StopCause";

// .NET TimeSpan "c" format, which the API's JSON serializer writes: [-][d.]hh:mm:ss[.fffffff]
const TIMESPAN_PATTERN = /^(-)?(?:(\d+)\.)?(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,7}))?$/;

export const parseDurationString = (durationString: string): TimeSpan | null => {
    if (!durationString) return null;

    const match = TIMESPAN_PATTERN.exec(durationString);
    if (!match) return null;

    const [, minus, daysStr, hoursStr, minutesStr, secondsStr, fractionStr] = match;
    const days = Number(daysStr ?? '0');
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    const seconds = Number(secondsStr);
    // The fraction is in ticks (100 ns): ".5" and ".5000000" are both 500 ms
    const fractionMilliseconds = fractionStr ? Number(fractionStr.padEnd(7, '0')) / 10000 : 0;
    if (hours > 23 || minutes > 59 || seconds > 59) return null;

    const totalMilliseconds =
        (days * 24 * 60 * 60 * 1000) +
        (hours * 60 * 60 * 1000) +
        (minutes * 60 * 1000) +
        (seconds * 1000) +
        fractionMilliseconds;
    if (!Number.isFinite(totalMilliseconds)) return null;

    // As in .NET, every component of a negative span is negative
    const signed = (value: number) => (minus && value !== 0 ? -value : value);

    return {
        days: signed(days),
        hours: signed(hours),
        minutes: signed(minutes),
        seconds: signed(seconds),
        milliseconds: signed(Math.floor(fractionMilliseconds)),
        totalMilliseconds: signed(totalMilliseconds)
    };
};

const calculateDuration = (startTime: Date, endTime: Date): TimeSpan => {
    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    const totalMs = end - start;

    if (totalMs < 0) {
        return {
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
            totalMilliseconds: 0
        };
    }

    const days = Math.floor(totalMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((totalMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((totalMs % (1000 * 60)) / 1000);
    const milliseconds = totalMs % 1000;

    return {
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
        totalMilliseconds: totalMs
    };
};

export const Random1000 = async (): Promise<string> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const url = `${apiUrl}/stopcause/1000randomStops`;

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "X-API-Key": "lksfjgskl5kad43"
            }
        });

        if (!response.ok) {
            console.error(
                `Failed to create random stop causes: ${response.status} - ${response.statusText}`
            );
            return "";
        }

        const data: string = await response.text(); // or response.json() if API returns JSON string
        return data;

    } catch (error) {
        console.error("Error creating random stop causes:", error);
        return "";
    }
};

const mapStopCauseResponse = (rawData: any[]): StopCause[] => {
    return rawData.map(item => {
        const startTime = new Date(item.startTime);
        const endTime = new Date(item.endTime);

        let duration: TimeSpan;
        if (item.duration && typeof item.duration === 'string') {
            const parsedDuration = parseDurationString(item.duration);
            duration = parsedDuration || calculateDuration(startTime, endTime);
        } else {
            duration = calculateDuration(startTime, endTime);
        }

        return {
            StartTime: startTime,
            EndTime: endTime,
            MachineId: item.machineId,
            MWC: item.mwc,
            WorkCenter: item.workCenter,
            MachineStopReason: item.machineStopReason,
            Alias: item.alias,
            Duration: duration,
            StopThreshold: item.stopThreshold,
            Id: item.id,
            Comment: item.comment || '',
            HasImage: item.hasImage || false,
            AttachedFileIds: item.attachedFileIds || [],
            CatalogueCode: item.catalogueCode || '',
        };
    });
};

// Returns null when the request fails or is aborted, so callers can tell a failure from no stops.
const fetchStopCauses = async (path: string, signal?: AbortSignal): Promise<StopCause[] | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const url = `${apiUrl}${path}`;

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "X-API-Key": "lksfjgskl5kad43"
            },
            signal
        });

        if (response.ok) {
            const rawData: any[] = await response.json();
            return mapStopCauseResponse(rawData);
        }

        console.error(
            `Failed to fetch stop causes: ${response.status} - ${response.statusText}`
        );
        return null;
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            return null;
        }
        console.error("Error fetching stop causes:", error);
        return null;
    }
};

export const GetAllStopCauses = async (): Promise<StopCause[]> => {
    return (await fetchStopCauses('/stopcause/analysis/historical')) ?? [];
};

export const GetStopsByWorkcenter = async (workcenter: string, signal?: AbortSignal): Promise<StopCause[] | null> => {
    return fetchStopCauses(`/stopcause/analysis/StopsByWorkcenter/${encodeURIComponent(workcenter)}`, signal);
};

export const GetStopsByMwc = async (mwc: string, signal?: AbortSignal): Promise<StopCause[] | null> => {
    return fetchStopCauses(`/stopcause/analysis/StopsByMwc/${encodeURIComponent(mwc)}`, signal);
};

export const GetStopsByMachineId = async (machineId: string, signal?: AbortSignal): Promise<StopCause[] | null> => {
    return fetchStopCauses(`/stopcause/analysis/StopsByMachineId/${encodeURIComponent(machineId)}`, signal);
};
